import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { urlValidator } from '~/core/validators/url/url.validator';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';

@Component({
  selector: 'app-organizer-timeslot-pre-schedule-live-url',
  templateUrl: './live-url.component.html',
})
export class OrganizerTimeslotPagePreScheduleLiveUrlComponent
  implements OnInit, OnDestroy
{
  private readonly subscriptions = new Subscription();

  private activity: Activity;
  private isSubmitButtonDisabled = false;

  public readonly ctaButtonType = CtaButtonTypeEnum;

  public formGroup: FormGroup;
  public isOverlayDisplayed = false;
  public isTabletDevice: boolean;
  public overlayStatus = SuccessStatusEnum.LOADING;

  constructor(
    private readonly matchMediaService: MatchMediaService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly locatableService: OrganizerSharedLocatableService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;

        this.formGroup = new FormGroup({
          liveUrl: new FormControl(this.activity.liveUrl || '', [
            Validators.required,
            urlValidator(),
            noHtmlTagValidator(),
          ]),
          noteForAttendees: new FormControl(
            this.activity.noteForAttendees || '',
            [noHtmlTagValidator()]
          ),
        });
      })
    );
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute(`/organizer.timeslot.pre-schedule/${this.activity.sku}`)
        .toString(),
    };
  }

  public get hasHeader(): boolean {
    return !this.isTabletDevice;
  }

  public get isButtonDisabled(): boolean {
    return this.formGroup.invalid && this.isSubmitButtonDisabled;
  }

  public submit(): void {
    this.isSubmitButtonDisabled = true;
    this.isOverlayDisplayed = true;

    const partialBody = {
      liveUrl: this.formGroup.value.liveUrl,
      noteForAttendees: [
        this.locatableService.buildCurrentLocatableBody(
          this.formGroup.value.noteForAttendees || null
        ),
      ],
    };

    this.subscriptions.add(
      this.organizerSharedActivityService
        .patchActivityById(this.activity.sku, partialBody)
        .pipe(
          mergeMap(() =>
            this.organizerSharedActivityService
              .getActivity(this.activity?.sku?.toString())
              .pipe(
                repeatUntil(
                  (activity) =>
                    activity.noteForAttendees ===
                      this.formGroup.value.noteForAttendees &&
                    activity.liveUrl === (this.formGroup.value.liveUrl || null)
                )
              )
          )
        )
        .subscribe(() => {
          this.overlayStatus = SuccessStatusEnum.SUCCESS;
        })
    );
  }
}
